import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  Info,
  SignOut,
  SpinnerGap,
  Paperclip,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { ADMIN_TOKEN_KEY } from './AdminLoginPage';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGES = {
  new: { label: 'جديد', color: '#1F3D2B', bg: '#1F3D2B1A' },
  in_progress: { label: 'قيد التجهيز', color: '#3D512D', bg: '#3D512D1A' },
  needs_info: { label: 'بانتظار العميل', color: '#B45309', bg: '#FEF3C7' },
  delivered: { label: 'تم التسليم', color: '#4A641F', bg: '#4A641F1A' },
};

const AdminRequestsPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetch(`${API_URL}/api/admin/service-requests`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (r) => {
        if (r.status === 401) {
          localStorage.removeItem(ADMIN_TOKEN_KEY);
          navigate('/admin/login');
          throw new Error('انتهت الجلسة');
        }
        if (!r.ok) throw new Error('فشل تحميل الطلبات');
        return r.json();
      })
      .then((d) => setItems(d.requests || []))
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    navigate('/admin/login');
  };

  return (
    <main
      data-testid="admin-requests-page"
      className="pt-24 pb-16 min-h-screen bg-[#F7F5F2]"
      dir="rtl"
    >
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1F3D2B] font-['IBM_Plex_Sans_Arabic']">
              طلبات الخدمات
            </h1>
            <p className="text-sm text-[#7A866E] mt-1">
              {items.length} طلب · جديد إلى قديم
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/talent-bank"
              data-testid="admin-talent-bank-link"
              className="text-sm text-[#1F3D2B] border border-[#D8D2C8] hover:bg-[#F5F2EC] rounded-xl px-4 py-2 transition-colors"
            >
              بنك الكفاءات →
            </Link>
            <Button
              onClick={logout}
              data-testid="admin-logout-btn"
              variant="outline"
              className="border-[#D8D2C8] text-[#1F3D2B] rounded-xl flex items-center gap-2"
            >
              <SignOut size={16} />
              خروج
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <SpinnerGap size={32} className="animate-spin text-[#1F3D2B] mx-auto" />
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-[24px] shadow-sm border border-[#E6E0D6] p-12 text-center">
            <Info size={36} className="text-[#7A866E] mx-auto mb-3" />
            <p className="text-[#1F3D2B] font-medium">لا توجد طلبات حتى الآن</p>
          </div>
        ) : (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.04 } } }}
            className="space-y-3"
          >
            {items.map((r) => {
              const badge = STATUS_BADGES[r.status] || STATUS_BADGES.new;
              return (
                <motion.div
                  key={r.id}
                  variants={{
                    hidden: { opacity: 0, y: 8 },
                    visible: { opacity: 1, y: 0 },
                  }}
                  data-testid={`admin-request-row-${r.id}`}
                  className="bg-white rounded-[18px] shadow-sm border border-[#E6E0D6] p-5 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span
                          className="inline-block px-3 py-1 rounded-full text-xs font-bold"
                          style={{ backgroundColor: badge.bg, color: badge.color }}
                        >
                          {badge.label}
                        </span>
                        <span className="text-xs text-[#7A866E] font-medium bg-[#F5F2EC] px-2 py-0.5 rounded">
                          {r.service_label}
                        </span>
                        {r.files_count > 0 && (
                          <span className="text-xs text-[#3D512D] inline-flex items-center gap-1">
                            <Paperclip size={12} /> {r.files_count}
                          </span>
                        )}
                      </div>
                      <p className="font-bold text-[#1F3D2B] truncate">
                        {r.name} <span className="text-[#7A866E] font-normal">— {r.organization}</span>
                      </p>
                      <p className="text-sm text-[#7A866E] mt-0.5" dir="ltr">
                        {r.email} · {r.phone}
                      </p>
                      <p className="text-xs text-[#92A082] mt-2">
                        {new Date(r.created_at).toLocaleString('ar-SA')}
                      </p>
                    </div>
                    <Link to={`/admin/requests/${r.id}`}>
                      <Button
                        data-testid={`open-request-${r.id}`}
                        className="bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl flex items-center gap-2"
                      >
                        فتح
                        <ArrowLeft size={16} className="rtl-flip" />
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </main>
  );
};

export default AdminRequestsPage;
